import { Divider, IconFont, Popover } from '@byecode/ui'
import type { FlowNode } from '@lighthouse/shared'
import {
    EndNodeType,
    NODE_CARD_WIDTH,
    TriggerNodeType,
    useAtomAction,
    useAtomAsyncAction,
    WorkflowTextMap,
    WorkflowType,
    X_OFFSET
} from '@lighthouse/shared'
import React, { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { addWorkflowAtom, fetchWorkflowListAtom } from '@/atoms/workflow/action'
import { workflowLabelMap, workflowOptions } from '@/constants/flow'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'
import * as srv from '@/services'
import { getWorkflowRunType } from '@/utils'

import { generateInitTriggerNode, generateNode } from '../FlowDetail/utils/addNode'
import { AllFlow } from './AllFlow'
import { RunLogList } from './RunLogList'
import * as SC from './styles'

interface FlowListProps {
    appId: string
}

type TabType = WorkflowType | 'all' | 'runLog'

export const FlowList: React.FC<FlowListProps> = ({ appId }) => {
    const navigate = useNavigate()

    const [tab, setTab] = useState<TabType>((sessionStorage.getItem('byecode-flow-tab') as TabType) || 'all')
    const [open, setOpen] = useState(false)
    const disabledWithVersion = useIsDisabledWithVersion()
    // const { run: updateWorkflow } = useAtomAsyncAction(fetchWorkflowListAtom)
    const { run: addWorkflow } = useAtomAction(addWorkflowAtom)
    const navigateToFlowDetail = useCallback(
        (flowId: string) => {
            navigate({ pathname: `./${flowId}` })
        },
        [navigate]
    )

    const handleTabChange = useCallback((val: TabType) => {
        setTab(val)
        sessionStorage.setItem('byecode-flow-tab', val)
    }, [])

    const handleFlowCreate = useCallback(
        async (type: WorkflowType) => {
            let flowInitNodes: FlowNode[] = []
            const endNode = generateNode(EndNodeType.END, {
                position: {
                    x: NODE_CARD_WIDTH * 2 + X_OFFSET * 2,
                    y: 0
                }
            })
            switch (type) {
                case WorkflowType.ACTION: {
                    const newNode = generateNode(TriggerNodeType.CLICK_TRIGGER)
                    flowInitNodes = [newNode, endNode]
                    break
                }
                case WorkflowType.SUB_PROCESS: {
                    const newNode = generateNode(TriggerNodeType.ARG_TRIGGER)
                    flowInitNodes = [newNode, endNode]
                    break
                }
                case WorkflowType.AUTOMATION: {
                    const newNode = generateInitTriggerNode() as FlowNode
                    flowInitNodes = [newNode, endNode]
                    break
                }
                default: {
                    break
                }
            }
            const newWorkflow = await srv.createFlow({
                name: `未命名${workflowLabelMap[type].label}`,
                type,
                runType: getWorkflowRunType(type),
                description: `未命名${workflowLabelMap[type].label}的描述`,
                edges: [],
                nodes: flowInitNodes
            })

            if (newWorkflow) {
                addWorkflow(newWorkflow)
                navigateToFlowDetail(newWorkflow.id)
                return true
            }

            return false
        },
        [addWorkflow, navigateToFlowDetail]
    )

    const content = useMemo(() => {
        switch (tab) {
            case 'all':
            case 'subProcess':
            case 'action':
            case 'automation': {
                return <AllFlow type={tab} onCardClick={navigateToFlowDetail} />
            }

            case 'runLog': {
                return <RunLogList appId={appId} />
            }

            default: {
                return null
            }
        }
    }, [appId, navigateToFlowDetail, tab])

    return (
        <SC.FlowListWrapper>
            <SC.FlowListTabWrapper>
                {!disabledWithVersion && (
                    <>
                        <Popover opened={open} onChange={setOpen} returnFocus={false}>
                            <Popover.Target>
                                <SC.FlowAdderWrapper>
                                    <SC.FlowAdderButton>
                                        <SC.FlowAdderButtonText>添加流程</SC.FlowAdderButtonText>
                                        <IconFont type="ArrowDownSmall" />
                                    </SC.FlowAdderButton>
                                </SC.FlowAdderWrapper>
                            </Popover.Target>
                            <Popover.Dropdown>
                                {workflowOptions.map(item => (
                                    <SC.FlowAdderDropdownItem key={item.value} onClick={() => handleFlowCreate(item.value)}>
                                        <SC.FlowAdderDropdownItemIconWrapper style={{ backgroundColor: item.color }}>
                                            <SC.FlowAdderDropdownItemIcon type={item.icon} size={20} />
                                        </SC.FlowAdderDropdownItemIconWrapper>
                                        {item.label}
                                    </SC.FlowAdderDropdownItem>
                                ))}
                            </Popover.Dropdown>
                        </Popover>
                        <Divider style={{ margin: '16px 0' }} color="var(--color-gray-200)" />
                    </>
                )}
                <SC.FlowListTab active={tab === 'all'} onClick={() => handleTabChange('all')}>
                    所有流程
                </SC.FlowListTab>
                <SC.FlowListTab active={tab === WorkflowType.AUTOMATION} onClick={() => handleTabChange(WorkflowType.AUTOMATION)}>
                    {WorkflowTextMap[WorkflowType.AUTOMATION]}
                </SC.FlowListTab>
                <SC.FlowListTab active={tab === WorkflowType.SUB_PROCESS} onClick={() => handleTabChange(WorkflowType.SUB_PROCESS)}>
                    {WorkflowTextMap[WorkflowType.SUB_PROCESS]}
                </SC.FlowListTab>
                <SC.FlowListTab active={tab === WorkflowType.ACTION} onClick={() => handleTabChange(WorkflowType.ACTION)}>
                    {WorkflowTextMap[WorkflowType.ACTION]}
                </SC.FlowListTab>
                <Divider my={12} color="var(--color-gray-200)" />
                <SC.FlowListTab active={tab === 'runLog'} onClick={() => handleTabChange('runLog')}>
                    运行日志
                </SC.FlowListTab>
            </SC.FlowListTabWrapper>
            <SC.FlowListRightContent>{content}</SC.FlowListRightContent>
        </SC.FlowListWrapper>
    )
}
